import { ReactNode } from 'react';
import { Select, SelectProps, SxProps, Theme } from '@mui/material';

import { useField } from 'formik';

import { getPaletteUtils, mixinSx } from '@watershed/style/styleUtils';

import type { FieldProps } from './Field';
import Field, { filterFieldInputProps } from './Field';
import clsx from 'clsx';

const FONT_SIZE_SMALL = 13;
const SVG_SIZE_SMALL = 12;
const SVG_RIGHT_PADDING_SMALL = 5;

const fieldSx: SxProps<Theme> = {
  margin: 0,
  width: '100%',
};

const inputSmallSx: SxProps<Theme> = {
  // Duplicate class selector for specificity.
  '& .MuiSelect-select.MuiSelect-select': {
    fontSize: FONT_SIZE_SMALL,
    padding: (theme) => theme.spacing(0, '20px', 0, 1) + '!important',
  },
  '& .MuiSelect-icon': {
    width: SVG_SIZE_SMALL,
    height: SVG_SIZE_SMALL,
    right: SVG_RIGHT_PADDING_SMALL,
  },
};

const inputWarningSx: SxProps<Theme> = {
  '& .MuiSelect-select': {
    boxShadow: (theme) => getPaletteUtils(theme.palette).boxShadowField.warning,
  },
};

const inputErrorSx: SxProps<Theme> = {
  '& .MuiSelect-select': {
    boxShadow: (theme) => getPaletteUtils(theme.palette).boxShadowField.error,
  },
};

export interface SelectFieldProps<T = unknown>
  extends Omit<FieldProps, 'inputId'>,
    Omit<SelectProps<T>, 'label' | 'ref'> {
  id: string;
  size?: 'small' | 'medium';
  fieldChildren?: ReactNode;
}

function getInputProps<T = unknown>(props: SelectFieldProps<T>) {
  const {
    id,
    validationState,
    validationMessage,
    sublabel,
    required,
    SelectDisplayProps,
  } = props;

  return {
    // Using sx prop directly
    sx: mixinSx(
      fieldSx,
      props.size === 'small' && inputSmallSx,
      validationState === 'error' && inputErrorSx,
      validationState === 'warning' && inputWarningSx
    ),
    SelectDisplayProps: {
      id,
      'aria-invalid': validationState === 'error',
      'aria-required': required,
      'aria-describedby': clsx(
        sublabel && `${id}-sublabel`,
        validationMessage && `${id}-validationMessage`
      ),
      ...SelectDisplayProps,
    },
    MenuProps: {
      ...(props.size === 'small'
        ? {
            sx: {
              '& .MuiMenu-list': {
                paddingX: 0,
                paddingY: 0.5,
                '& .MuiMenuItem-root': {
                  fontSize: FONT_SIZE_SMALL,
                  paddingX: 1,
                  paddingY: 0.25,
                },
              },
            },
          }
        : {}),
      ...(props.MenuProps ?? {}),
    },
  };
}

export default function SelectField({
  size = 'medium',
  className,
  children,
  ...rawProps
}: SelectFieldProps) {
  const [formikProps, meta] = useField({
    name: rawProps.name ?? rawProps.id,
    value: rawProps.value,
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore Formik is expecting input events
    onChange: rawProps.onChange,
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore Formik is expecting input events
    onBlur: rawProps.onBlur,
  });

  const props: SelectFieldProps = {
    validationState: meta.touched && meta.error ? 'error' : 'default',
    validationMessage: meta.touched && meta.error ? meta.error : undefined,
    ...rawProps,
  };

  const finalProps = {
    ...formikProps,
    ...filterFieldInputProps(rawProps),
    ...getInputProps({ size, ...props }),
  };

  return (
    <Field {...props} inputId={rawProps.id} className={className}>
      <Select
        data-testid={`select-${rawProps.id}`}
        {...finalProps}
        children={children}
        value={
          finalProps.multiple ? (finalProps.value ?? []) : finalProps.value
        }
      />
    </Field>
  );
}

export function SelectFieldNonFormik<T>({
  size = 'medium',
  className,
  children,
  fieldChildren,
  ...props
}: SelectFieldProps<T>) {
  return (
    <Field {...props} inputId={props.id} className={className}>
      {fieldChildren ?? (
        <Select
          data-testid={`select-${props.id}`}
          {...filterFieldInputProps(props)}
          {...getInputProps({ size, ...props })}
          children={children}
        />
      )}
    </Field>
  );
}

export function SelectBase<T>({
  size,
  children,
  ...props
}: SelectFieldProps<T>) {
  return (
    <Select
      {...props}
      {...getInputProps({ size, ...props })}
      data-testid={`select-${props.id}`}
      children={children}
    />
  );
}
